import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import ImageSlider from '../components/ui/ImageSlider';
import { services } from '../utils/mockData';

const Services = () => {
  const { serviceId } = useParams();

  /* =============================
     SERVICE DETAIL PAGE
  ============================== */
  if (serviceId) {
    if (serviceId === 'commercial') {
      return <Navigate to="/contact" replace />;
    }

    const service = services.find((s) => s.id === serviceId);
    if (!service) {
      return <div className="pt-32 text-center">Service not found</div>;
    }

    return (
      <div className="min-h-screen pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                {service.popular && (
                  <span className="inline-block bg-blue-600 px-4 py-2 rounded-full text-sm font-bold mb-4">
                    MOST POPULAR!
                  </span>
                )}

                <h1 className="text-5xl font-bold mb-4">{service.name}</h1>
                <p className="text-xl text-slate-300 mb-6">
                  {service.description}
                </p>

                <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700">
                  <Link to="/book">
                    Book Now <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
              </div>

              {/* 🔥 Swiper Slider */}
              <ImageSlider images={service.image} />
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-8">
              What's Included
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              {service.features.map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-blue-600 mt-1" />
                  <p className="text-slate-700">{feature}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-slate-50 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Ready to book?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Get an instant quote and schedule your {service.name.toLowerCase()} today.
          </p>

          <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700">
            <Link to="/book">Book Now</Link>
          </Button>
        </section>
      </div>
    );
  }

  /* =============================
     SERVICES LIST PAGE
  ============================== */
  return (
    <div className="min-h-screen pt-20">
      <section className="py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 text-center">
        <h1 className="text-5xl font-bold text-slate-900 mb-6">
          Our Cleaning Services
        </h1>
        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
          Once-off or recurring professional cleaning tailored to your needs.
        </p>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service) => (
              <Card
                key={service.id}
                className="overflow-hidden hover:shadow-2xl transition-all"
              >
                {/* First image only */}
                <div className="aspect-video overflow-hidden">
                  <img
                    src={service.image[0]}
                    alt={service.name}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>

                <div className="p-8">
                  <h3 className="text-2xl font-bold mb-3">{service.name}</h3>
                  <p className="text-slate-600 mb-6">{service.description}</p>

                  <div className="flex gap-3">
                    <Button asChild className="flex-1 bg-blue-600 hover:bg-blue-700">
                      <Link to={service.id === 'commercial' ? '/contact' : '/book'}>
                        Book Now
                      </Link>
                    </Button>

                    <Button asChild variant="outline" className="flex-1">
                      <Link
                        to={
                          service.id === 'commercial'
                            ? '/contact'
                            : `/services/${service.id}`
                        }
                      >
                        {service.id === 'commercial' ? 'Contact Us' : 'Learn More'}
                      </Link>
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
